import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Eye, Pencil, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader } from '@/layouts/AppLayout';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDateOnly, formatInt } from '@/lib/utils';

// ── TanStack Table type augmentation ────────────────────────────────────────

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'right';
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    navigate: ReturnType<typeof useNavigate>;
    cloneById: (id: string) => void;
    cloneIsPending: boolean;
    deleteById: (id: string, code: string) => void;
    deleteIsPending: boolean;
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

type BookingStatus =
  | 'draft'
  | 'tentative'
  | 'confirmed'
  | 'voucher_issued'
  | 'paid'
  | 'completed'
  | 'cancelled'
  | 'released';

interface BookingRoomSummary {
  roomsCount: number;
  mealPlan: string;
  ratePerNight: string;
  rateSource: string;
}

interface BookingRow {
  id: string;
  bookingCode: string;
  hotel: { id: string; name: string; city: string };
  client: { id: string; name: string; type: string };
  guestName: string;
  checkIn: string;
  checkOut: string;
  nights: number | null;
  totalRoomNights: number;
  amount: string;
  confNo: string | null;
  bookerUser: { id: string; fullName: string } | null;
  status: BookingStatus;
  tentativeUntil: string | null;
  cancellationReason: string | null;
  remark: string | null;
  createdAt: string;
  updatedAt: string | null;
  bookingRooms: BookingRoomSummary[];
}

interface BookingsPage {
  data: BookingRow[];
  meta: { total: number; page: number; limit: number };
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUSES: BookingStatus[] = [
  'draft',
  'tentative',
  'confirmed',
  'voucher_issued',
  'paid',
  'completed',
  'cancelled',
  'released',
];

const STATUS_LABELS: Record<BookingStatus, string> = {
  draft: 'Draft',
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  voucher_issued: 'Voucher Issued',
  paid: 'Paid',
  completed: 'Completed',
  cancelled: 'Cancelled',
  released: 'Released',
};

// ── Column definitions ────────────────────────────────────────────────────────
// Order mirrors the source Excel exactly:
// Hotel · Guest · Check-in · Check-out · Rooms · Nights · Total RN ·
// Rate · Meal · Booker · Client · Conf No. · Amount · Status · Remark
// then the sticky-right actions column.

const COLUMNS: ColumnDef<BookingRow>[] = [
  {
    id: 'hotel',
    header: 'Hotel',
    cell: ({ row }) => (
      <Link
        to={`/hotels/${row.original.hotel.id}`}
        onClick={(e) => e.stopPropagation()}
        className="block max-w-[120px] truncate font-medium text-neutral-900 hover:text-tangerine-600"
      >
        {row.original.hotel.name}
      </Link>
    ),
  },
  {
    id: 'guestName',
    header: 'Guest',
    cell: ({ row }) => (
      <span className="block max-w-[140px] truncate">{row.original.guestName}</span>
    ),
  },
  {
    id: 'checkIn',
    header: 'Check-in',
    cell: ({ row }) => formatDateOnly(row.original.checkIn),
  },
  {
    id: 'checkOut',
    header: 'Check-out',
    cell: ({ row }) => formatDateOnly(row.original.checkOut),
  },
  {
    id: 'rooms',
    header: 'Rooms',
    meta: { align: 'right' },
    cell: ({ row }) => {
      const total = row.original.bookingRooms.reduce((s, r) => s + r.roomsCount, 0);
      return formatInt(total);
    },
  },
  {
    id: 'nights',
    header: 'Nights',
    meta: { align: 'right' },
    cell: ({ row }) => formatInt(row.original.nights),
  },
  {
    id: 'totalRoomNights',
    header: 'Total RN',
    meta: { align: 'right' },
    cell: ({ row }) => formatInt(row.original.totalRoomNights),
  },
  {
    id: 'rate',
    header: 'Avg Rate',
    meta: { align: 'right' },
    cell: ({ row }) => {
      const rooms = row.original.bookingRooms;
      if (!rooms.length) return '—';
      const totalRooms = rooms.reduce((s, r) => s + r.roomsCount, 0);
      const weightedSum = rooms.reduce(
        (s, r) => s + parseFloat(r.ratePerNight) * r.roomsCount,
        0,
      );
      return formatCurrency(String(totalRooms > 0 ? weightedSum / totalRooms : 0));
    },
  },
  {
    id: 'mealPlan',
    header: 'Meal',
    cell: ({ row }) => {
      const plans = [...new Set(row.original.bookingRooms.map((r) => r.mealPlan))];
      if (plans.length === 0) return <span>—</span>;
      if (plans.length === 1) return <span>{plans[0].toUpperCase()}</span>;
      return (
        <span
          title={plans.map((p) => p.toUpperCase()).join(', ')}
          className="cursor-default underline decoration-dotted"
        >
          Multi
        </span>
      );
    },
  },
  {
    id: 'booker',
    header: 'Booker',
    cell: ({ row }) => (
      <span className="block max-w-[110px] truncate">
        {row.original.bookerUser?.fullName ?? '—'}
      </span>
    ),
  },
  {
    id: 'client',
    header: 'Client',
    cell: ({ row }) => (
      <Link
        to={`/clients/${row.original.client.id}`}
        onClick={(e) => e.stopPropagation()}
        className="block max-w-[120px] truncate hover:text-tangerine-600"
      >
        {row.original.client.name}
      </Link>
    ),
  },
  {
    id: 'confNo',
    header: 'Conf No.',
    cell: ({ row }) => (
      <span className="text-neutral-500">{row.original.confNo ?? '—'}</span>
    ),
  },
  {
    id: 'amount',
    header: 'Amount',
    meta: { align: 'right' },
    cell: ({ row }) => (
      <span className="font-medium">{formatCurrency(row.original.amount)}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={row.original.status}>{STATUS_LABELS[row.original.status]}</Badge>
    ),
  },
  {
    id: 'remark',
    header: 'Remark',
    cell: ({ row }) => {
      const v = row.original.remark;
      return v ? (
        <span
          className="block max-w-[160px] truncate text-neutral-500"
          title={v}
        >
          {v}
        </span>
      ) : (
        '—'
      );
    },
  },
  // Actions column — sticky right, invisible until row hover
  {
    id: 'actions',
    header: '',
    cell: ({ row, table }) => {
      const { navigate, cloneById, cloneIsPending, deleteById, deleteIsPending } =
        table.options.meta!;
      const b = row.original;
      return (
        <div className="flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100 group-focus-within:opacity-100">
          <ActionIcon
            title="View"
            onClick={() => navigate(`/bookings/${b.id}`)}
          >
            <Eye className="h-3.5 w-3.5" />
          </ActionIcon>
          <ActionIcon
            title="Edit"
            onClick={() => navigate(`/bookings/${b.id}/edit`)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </ActionIcon>
          <ActionIcon
            title="Clone to new draft"
            onClick={() => cloneById(b.id)}
            disabled={cloneIsPending}
            hoverClass="hover:text-tangerine-600 hover:bg-tangerine-50"
          >
            <Copy className="h-3.5 w-3.5" />
          </ActionIcon>
          <ActionIcon
            title="Delete"
            onClick={() => deleteById(b.id, b.bookingCode)}
            disabled={deleteIsPending}
            hoverClass="hover:text-danger-700 hover:bg-danger-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </ActionIcon>
        </div>
      );
    },
  },
];

// ── Action icon button ────────────────────────────────────────────────────────

function ActionIcon({
  children,
  title,
  onClick,
  disabled,
  hoverClass = 'hover:text-neutral-700 hover:bg-neutral-100',
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  hoverClass?: string;
}) {
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded text-neutral-400 transition-colors disabled:opacity-40',
        hoverClass,
      )}
    >
      {children}
    </button>
  );
}

// ── Pagination helpers ────────────────────────────────────────────────────────

function clampPage(raw: string | null, fallback = 1) {
  const n = parseInt(raw ?? '', 10);
  return Number.isFinite(n) && n >= 1 ? n : fallback;
}

function clampLimit(raw: string | null, fallback = 50) {
  const n = parseInt(raw ?? '', 10);
  return [25, 50, 100].includes(n) ? n : fallback;
}

// ── Main component ────────────────────────────────────────────────────────────

export function BookingsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  // Read filter/pagination state from URL
  const q = params.get('q') ?? '';
  const statusFilter = params.get('status') ?? '';
  const page = clampPage(params.get('page'));
  const limit = clampLimit(params.get('limit'));

  // Local input state — debounced write to URL 'q' param
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    const t = setTimeout(() => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const trimmed = searchInput.trim();
          trimmed ? next.set('q', trimmed) : next.delete('q');
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Sync URL 'q' → local input when navigating back with browser buttons
  useEffect(() => {
    setSearchInput(q);
  // Only re-sync when the URL param actually changes externally
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Build query string for the API call
  const apiParams = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set('search', q);
    if (statusFilter) p.set('status', statusFilter);
    p.set('page', String(page));
    p.set('limit', String(limit));
    p.set('sortBy', 'createdAt');
    p.set('sortOrder', 'desc');
    return p.toString();
  }, [q, statusFilter, page, limit]);

  const { data: result, isLoading, isFetching } = useQuery({
    queryKey: ['bookings', { q, statusFilter, page, limit }],
    queryFn: () => api.get<BookingsPage>(`/bookings?${apiParams}`),
    placeholderData: (prev) => prev,
  });

  const rows = result?.data ?? [];
  const meta = result?.meta ?? { total: 0, page: 1, limit };
  const pageCount = Math.max(1, Math.ceil(meta.total / limit));

  // ── Mutations ────────────────────────────────────────────────────────────

  const cloneMutation = useMutation({
    mutationFn: (id: string) => api.post<BookingRow>(`/bookings/${id}/clone`),
    onSuccess: (newBooking) => {
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      toast.success(`Cloned → ${newBooking.bookingCode}`);
      navigate(`/bookings/${newBooking.id}`);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Clone failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/bookings/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      toast.success('Booking deleted');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  // ── URL helpers ──────────────────────────────────────────────────────────

  function setFilter(key: string, value: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        value ? next.set(key, value) : next.delete(key);
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }

  function goToPage(p: number) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', String(p));
        return next;
      },
      { replace: true },
    );
  }

  // ── Table instance ───────────────────────────────────────────────────────

  const table = useReactTable({
    data: rows,
    columns: COLUMNS,
    pageCount,
    state: { pagination: { pageIndex: page - 1, pageSize: limit } },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: page - 1, pageSize: limit })
          : updater;
      goToPage(next.pageIndex + 1);
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      navigate,
      cloneById: (id) => cloneMutation.mutate(id),
      cloneIsPending: cloneMutation.isPending,
      deleteById: (id, code) => {
        if (!confirm(`Delete booking ${code}? This cannot be undone.`)) return;
        deleteMutation.mutate(id);
      },
      deleteIsPending: deleteMutation.isPending,
    },
  });

  // ── Render ───────────────────────────────────────────────────────────────

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, meta.total);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Bookings"
        description="All hotel bookings"
        action={
          <Button asChild>
            <Link to="/bookings/new">New booking</Link>
          </Button>
        }
      />

      {/* Filter bar */}
      <div className="flex items-center gap-3 border-b border-neutral-200 bg-white px-6 py-3">
        <label htmlFor="search-bookings" className="sr-only">Search bookings</label>
        <Input
          id="search-bookings"
          placeholder="Search code, guest, conf no…"
          className="w-72"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Select
          value={statusFilter || '_all'}
          onValueChange={(v) => setFilter('status', v === '_all' ? '' : v)}
        >
          <SelectTrigger className="w-44" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isFetching && !isLoading && <Spinner className="h-4 w-4 shrink-0" />}
      </div>

      {/* Table scroll area */}
      <div className="min-h-0 flex-1 overflow-auto bg-white">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spinner className="h-6 w-6" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-sm text-neutral-400">
            {q || statusFilter
              ? 'No bookings match your filters.'
              : 'No bookings yet. '}
            {!q && !statusFilter && (
              <Link to="/bookings/new" className="text-tangerine-600 hover:underline">
                Create the first one.
              </Link>
            )}
          </div>
        ) : (
          <table
            aria-label="Bookings"
            className="w-full border-collapse text-sm"
            style={{ minWidth: '1440px' }}
          >
            <thead className="sticky top-0 z-10 border-b border-neutral-200 bg-neutral-50">
              <tr>
                {table.getFlatHeaders().map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cn(
                      'whitespace-nowrap border-r border-neutral-100 px-3 py-2.5 text-xs font-medium text-neutral-500 last:border-0',
                      header.column.columnDef.meta?.align === 'right'
                        ? 'text-right'
                        : 'text-left',
                      header.column.id === 'actions'
                        ? 'sticky right-0 z-10 w-[108px] border-l border-neutral-200 bg-neutral-50'
                        : '',
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={0}
                  className="group cursor-pointer transition-colors hover:bg-tangerine-50 focus:bg-tangerine-50 focus:outline-none"
                  onClick={() => navigate(`/bookings/${row.original.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/bookings/${row.original.id}`);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        'whitespace-nowrap px-3 py-2.5 text-neutral-700',
                        cell.column.columnDef.meta?.align === 'right'
                          ? 'text-right tabular-nums'
                          : '',
                        cell.column.id === 'actions'
                          ? 'sticky right-0 z-10 w-[108px] border-l border-neutral-100 bg-white transition-colors group-hover:bg-tangerine-50'
                          : '',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination bar */}
      {!isLoading && (
        <div className="flex items-center justify-between border-t border-neutral-200 bg-white px-6 py-3 text-sm">
          <span className="text-neutral-500" aria-live="polite" aria-atomic="true">
            {meta.total === 0
              ? 'No results'
              : `${formatInt(start)}–${formatInt(end)} of ${formatInt(meta.total)} bookings`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              ← Prev
            </Button>
            <span className="min-w-[80px] text-center text-neutral-600" aria-live="polite">
              {page} / {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(page + 1)}
              disabled={page >= pageCount}
              aria-label="Next page"
            >
              Next →
            </Button>
            <Select
              value={String(limit)}
              onValueChange={(v) => setFilter('limit', v)}
            >
              <SelectTrigger className="h-8 w-[90px] text-xs" aria-label="Rows per page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
                <SelectItem value="100">100 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}
